import { ReactNode } from "react";
import { GrClose } from "react-icons/gr";
import { ProgressBar } from "../../components/ProgressBar";
import { Box } from "../../components/Box";
import {
  Breadcrumb,
  StaticBreadcrumb,
  Crumb,
  BreadcrumbDivider,
} from "../../components/Breadcrumbs";
import { Flex } from "../../components/Flex";
import { IconButton } from "../../components/IconButton";
import Topbar from "../../components/Topbar";
import { ROUTE } from "../../routes";
import { useNavigate } from "react-router-dom";

type ProfileMode = "entity" | "person";
type EntityStep = "entityDetails" | "address" | "createProfile";
type PersonStep = "personDetails" | "address" | "summary";

type NewProfileFrameProps =
  | {
      mode: "entity";
      step: EntityStep;
      children: ReactNode;
    }
  | {
      mode: "person";
      step: PersonStep;
      children: ReactNode;
    };

export const NewProfileFrame = ({
  mode,
  step,
  children,
}: NewProfileFrameProps) => {
  const navigate = useNavigate();

  const entitySteps: EntityStep[] = [
    "entityDetails",
    "address",
    "createProfile",
  ];
  const personSteps: PersonStep[] = ["personDetails", "address", "summary"];

  const steps = mode === "entity" ? entitySteps : personSteps;

  const titlesMap: Record<ProfileMode, Record<string, string>> = {
    entity: {
      entityDetails: "Entity details",
      address: "Add address",
      createProfile: "Create profile",
    },
    person: {
      personDetails: "Person Details",
      address: "Add address",
      summary: "Summary",
    },
  };

  const currentStepIndex =
    mode === "entity"
      ? entitySteps.indexOf(step as EntityStep)
      : personSteps.indexOf(step as PersonStep);

  const progress = (currentStepIndex + 1) * (100 / steps.length);

  const breadcrumbs = steps.map((currentStep, index) => ({
    isActive: step === currentStep,
    title: `${String(index + 1).padStart(2, "0")} ${
      titlesMap[mode][currentStep]
    }`,
    key: index + 1,
  }));

  const headerRootLabel = mode === "entity" ? "Entities" : "Profiles";
  const headerCreateLabel =
    mode === "entity" ? "Create a new entity" : "Create a new person";

  return (
    <div>
      <Topbar
        header={
          <Flex justifyContent="space-between" width="100%">
            <>
              <Breadcrumb>
                <Breadcrumb.Section linkTo={ROUTE.PROFILES}>
                  {headerRootLabel}
                </Breadcrumb.Section>
                <Breadcrumb.Section>{headerCreateLabel}</Breadcrumb.Section>
              </Breadcrumb>
            </>
            <Flex>
              <IconButton
                Type={GrClose}
                onClick={() => navigate(ROUTE.PROFILES)}
                color="gray.40"
                ml={4}
                mr={3}
              />
            </Flex>
          </Flex>
        }
      />
      <Box height="calc(100vh - 155px)" overflowY="auto">
        <Box>
          <ProgressBar progress={progress} />
          <Box padding={2} marginLeft={2}>
            <StaticBreadcrumb>
              {breadcrumbs.map(({ isActive, title, key }, index) => (
                <Crumb
                  key={key}
                  isActive={isActive}
                  iconDivider={
                    index < breadcrumbs.length - 1 && <BreadcrumbDivider />
                  }
                >
                  {title}
                </Crumb>
              ))}
            </StaticBreadcrumb>
          </Box>
        </Box>
        <Flex justifyContent="center" alignItems="center">
          <Box>{children}</Box>
        </Flex>
      </Box>
    </div>
  );
};
